// distancing-cowork 의 /task/guides endpoint wrapper.
// REACT_APP_AGENT_API_HOST 미설정 시 in-memory mock (개발 fallback) — taskWorkingApi 와 동일 패턴.
//
// 가이드(tone/principles)는 백엔드 디스크 기본값(source="default") 위에 override(source="override")를
// 얹는 구조다. agent 는 read_file 로 가이드를 참고하며, 저장 즉시 다음 작성/검토 턴부터 반영된다.
//
// 동시 편집은 working_task 와 동일한 `version` optimistic lock — PUT 시 expected_version mismatch 면
// 409 + 현재 override 반환. 호출자는 GuideVersionConflictError 를 catch 해 최신본을 따라간다.
// (working_task 와 마찬가지로 본 endpoint 는 인증 미도입 — task editor 접근 권한은 FE 가 게이팅.)

const HOST = process.env.REACT_APP_AGENT_API_HOST || "";

export type GuideName = "tone" | "principles";

export interface GuideDTO {
  name: GuideName;
  path: string;
  content: string;
  version: number; // 0 = override 없음(디스크 기본값)
  source: "default" | "override";
  updated_by: string | null;
  updated_at: string | null;
}

export class GuideVersionConflictError extends Error {
  /** 409 응답 본문 — store 의 현재 (다른 editor 가 저장한) override. */
  current: GuideDTO;
  constructor(current: GuideDTO) {
    super(
      `guide version mismatch (name=${current.name}, current version=${current.version})`
    );
    this.name = "GuideVersionConflictError";
    this.current = current;
  }
}

const GUIDE_NAMES: GuideName[] = ["tone", "principles"];

// =========================
// 네트워크 재시도 / 에러 분류 (GET 전용)
// =========================
//
// 브라우저는 모든 네트워크 레벨 실패(DNS·연결거부·CORS·타임아웃·오프라인)를
// 동일한 `TypeError: Failed to fetch` 하나로 뭉뚱그린다 — catch 만으론 원인 구분 불가.
// 백오피스 디버깅을 위해 AbortController(타임아웃)·navigator.onLine 으로 직접 분류한다.

const GUIDES_TIMEOUT_MS = 8000;
const GUIDES_RETRIES = 2; // 총 3회 시도 (GET 은 멱등이라 일시적 블립에 한해 재시도)

function describeFetchError(e: unknown): string {
  const name = e instanceof Error ? e.name : "";
  if (name === "AbortError") {
    return `요청 타임아웃 (${GUIDES_TIMEOUT_MS / 1000}초 내 응답 없음)`;
  }
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return "네트워크 오프라인 — 인터넷 연결을 확인해주세요";
  }
  if (e instanceof TypeError) {
    return "서버에 연결하지 못했습니다 (네트워크/서버 무응답)";
  }
  return e instanceof Error ? e.message : "알 수 없는 오류";
}

// 서버가 4xx/5xx 로 "응답" 한 경우는 재시도해도 같으므로 즉시 실패시킨다.
// 이 메시지 접두로 HTTP 에러와 네트워크 실패를 구분한다.
const HTTP_ERROR_PREFIX = "listGuides 실패: 서버";

export async function getGuidesWithRetry(url: string): Promise<GuideDTO[]> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= GUIDES_RETRIES; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), GUIDES_TIMEOUT_MS);
    try {
      const res = await fetch(url, {
        method: "GET",
        signal: controller.signal,
      });
      if (!res.ok) {
        throw new Error(`${HTTP_ERROR_PREFIX} ${res.status} 응답`);
      }
      return (await res.json()) as GuideDTO[];
    } catch (e) {
      lastError = e;
      // HTTP 에러는 재시도 없이 그대로 전파.
      if (e instanceof Error && e.message.startsWith(HTTP_ERROR_PREFIX)) {
        throw e;
      }
      if (attempt === GUIDES_RETRIES) break;
      await new Promise((r) => setTimeout(r, 300 * 2 ** attempt));
    } finally {
      clearTimeout(timer);
    }
  }
  throw new Error(
    `listGuides 실패: ${describeFetchError(lastError)} (재시도 ${GUIDES_RETRIES}회 후 포기)`
  );
}

// =========================
// In-memory mock (HOST 미설정 시)
// =========================

const MOCK_PATHS: Record<GuideName, string> = {
  tone: "/guides/02-tone.md",
  principles: "/guides/04-principles.md",
};

// HOST 없으면 실제 디스크 기본값을 못 읽으므로 placeholder 를 보여준다.
const MOCK_DEFAULTS: Record<GuideName, string> = {
  tone: "# 톤 가이드 (mock 기본값)\n\nREACT_APP_AGENT_API_HOST 가 설정되면 실제 백엔드 기본값을 불러옵니다.",
  principles:
    "# 제작 원칙 (mock 기본값)\n\nREACT_APP_AGENT_API_HOST 가 설정되면 실제 백엔드 기본값을 불러옵니다.",
};

const mockOverrides = new Map<GuideName, GuideDTO>();

function mockNow(): string {
  return new Date().toISOString();
}

function mockEffective(name: GuideName): GuideDTO {
  const override = mockOverrides.get(name);
  if (override) return override;
  return {
    name,
    path: MOCK_PATHS[name],
    content: MOCK_DEFAULTS[name],
    version: 0,
    source: "default",
    updated_by: null,
    updated_at: null,
  };
}

// =========================
// Public API
// =========================

export async function listGuides(): Promise<GuideDTO[]> {
  if (!HOST) return GUIDE_NAMES.map(mockEffective);
  return getGuidesWithRetry(`${HOST}/task/guides`);
}

export async function putGuide(
  name: GuideName,
  content: string,
  expectedVersion: number
): Promise<GuideDTO> {
  if (!HOST) {
    const currentVersion = mockOverrides.get(name)?.version ?? 0;
    if (currentVersion !== expectedVersion) {
      throw new GuideVersionConflictError(mockEffective(name));
    }
    const next: GuideDTO = {
      name,
      path: MOCK_PATHS[name],
      content,
      version: currentVersion + 1,
      source: "override",
      updated_by: null,
      updated_at: mockNow(),
    };
    mockOverrides.set(name, next);
    return next;
  }
  const res = await fetch(`${HOST}/task/guides/${name}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content, expected_version: expectedVersion }),
  });
  if (res.status === 409) {
    throw new GuideVersionConflictError((await res.json()) as GuideDTO);
  }
  if (!res.ok) {
    throw new Error(`putGuide failed: ${res.status}`);
  }
  return (await res.json()) as GuideDTO;
}

// =========================
// 테스트용 — mock state 리셋
// =========================

export function __resetGuidesMockForTests(): void {
  mockOverrides.clear();
}
